import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Button, Spinner, Tabs, Tab, Badge, Alert, Table } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '../../services/api';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  parseISO
} from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ENV_CONFIG } from '../../config/environment';

const ALLERGIES_OPTIONS = [
  'No known allergies',
  'Peanut',
  'Milk',
  'Egg',
  'Fish',
  'Shellfish',
  'Soy',
  'Wheat',
  'Dust',
  'Pollen',
  'Animal dander',
  'Insect bites',
  'Medication',
  'Other'
];

const ATTENDANCE_STATUS_VARIANTS = {
  present: 'success',
  absent: 'danger',
  late: 'warning',
  half_day: 'info'
};

const weekDayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const formatTime = (time) => {
  if (!time) return '-';
  try {
    return format(parseISO(`1970-01-01T${time}`), 'hh:mm a');
  } catch (error) {
    return time;
  }
};

const formatDisplayDate = (dateValue, fallback = 'N/A') => {
  if (!dateValue) return fallback;
  try {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (Number.isNaN(date.getTime())) return fallback;
    return format(date, 'dd MMMM yyyy');
  } catch (error) {
    return fallback;
  }
};

const ParentStudentDetails = ({ studentId }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [formValues, setFormValues] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const defaultTableStart = format(subMonths(new Date(), 1), 'yyyy-MM-dd');
  const defaultTableEnd = format(new Date(), 'yyyy-MM-dd');
  const [tableStartInput, setTableStartInput] = useState(defaultTableStart);
  const [tableEndInput, setTableEndInput] = useState(defaultTableEnd);
  const [tableRange, setTableRange] = useState({ start: defaultTableStart, end: defaultTableEnd });

  const [syllabusStart, setSyllabusStart] = useState('');
  const [syllabusEnd, setSyllabusEnd] = useState('');

  const { data: studentDetailsData, isLoading: loadingStudentDetails } = useQuery({
    queryKey: ['parent_student_details', studentId],
    queryFn: async () => {
      if (!studentId) return null;
      const response = await apiService.get(`/api/parent/students/${studentId}`);
      return response.data || null;
    },
    enabled: !!studentId
  });

  const studentDetails = studentDetailsData || null;

  useEffect(() => {
    if (!studentDetails) {
      setFormValues(null);
      return;
    }

    const allergiesValue = (() => {
      if (Array.isArray(studentDetails.allergies)) {
        return studentDetails.allergies;
      }
      if (typeof studentDetails.allergies === 'string' && studentDetails.allergies.trim() !== '') {
        try {
          const parsed = JSON.parse(studentDetails.allergies);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }
      return [];
    })();

    setFormValues({
      student_name: studentDetails.student_name || '',
      roll_number: studentDetails.roll_number || '',
      admission_date: studentDetails.admission_date || '',
      grade_name: studentDetails.grade_name || '',
      division_name: studentDetails.division_name || '',
      gender: studentDetails.gender || '',
      residential_address: studentDetails.residential_address || '',
      pincode: studentDetails.pincode || '',
      sam_samagrah_id: studentDetails.sam_samagrah_id || '',
      aapar_id: studentDetails.aapar_id || '',
      emergency_contact_number: studentDetails.emergency_contact_number || '',
      emergency_contact_name: studentDetails.emergency_contact_name || '',
      emergency_contact_relationship: studentDetails.emergency_contact_relationship || '',
      travel_mode: studentDetails.travel_mode || '',
      parent_or_staff_name: studentDetails.parent_or_staff_name || '',
      verified_tts_id: studentDetails.verified_tts_id || '',
      allergies: allergiesValue,
      diabetic: !!studentDetails.diabetic,
      lifestyle_diseases: studentDetails.lifestyle_diseases || '',
      asthmatic: !!studentDetails.asthmatic,
      phobia: !!studentDetails.phobia,
      doctor_name: studentDetails.doctor_name || '',
      doctor_contact: studentDetails.doctor_contact || '',
      clinic_address: studentDetails.clinic_address || '',
      blood_group: studentDetails.blood_group || '',
      student_aspirations: studentDetails.student_aspirations || '',
      special_need: studentDetails.special_need || ''
    });
  }, [studentDetails]);

  const updateStudentMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await apiService.put(`/api/parent/students/${studentId}`, payload);
      return response;
    },
    onSuccess: () => {
      toast.success('Student information updated');
      queryClient.invalidateQueries(['parent_student_details', studentId]);
      queryClient.invalidateQueries(['parent_students']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update student information');
    }
  });

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAllergyToggle = (option) => {
    setFormValues((prev) => {
      const current = Array.isArray(prev?.allergies) ? [...prev.allergies] : [];
      const index = current.indexOf(option);
      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(option);
      }
      return {
        ...prev,
        allergies: current
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!formValues) return;

    const payload = {
      student_name: formValues.student_name,
      gender: formValues.gender,
      residential_address: formValues.residential_address,
      pincode: formValues.pincode,
      sam_samagrah_id: formValues.sam_samagrah_id,
      aapar_id: formValues.aapar_id,
      emergency_contact_number: formValues.emergency_contact_number,
      emergency_contact_name: formValues.emergency_contact_name,
      emergency_contact_relationship: formValues.emergency_contact_relationship,
      travel_mode: formValues.travel_mode,
      parent_or_staff_name: formValues.parent_or_staff_name,
      verified_tts_id: formValues.verified_tts_id,
      allergies: formValues.allergies || [],
      diabetic: formValues.diabetic,
      lifestyle_diseases: formValues.lifestyle_diseases,
      asthmatic: formValues.asthmatic,
      phobia: formValues.phobia,
      doctor_name: formValues.doctor_name,
      doctor_contact: formValues.doctor_contact,
      clinic_address: formValues.clinic_address,
      blood_group: formValues.blood_group,
      student_aspirations: formValues.student_aspirations,
      special_need: formValues.special_need
    };

    updateStudentMutation.mutate(payload);
  };

  const monthStart = startOfMonth(calendarMonth);
  const monthEnd = endOfMonth(calendarMonth);

  const calendarParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append('start_date', format(monthStart, 'yyyy-MM-dd'));
    params.append('end_date', format(monthEnd, 'yyyy-MM-dd'));
    return params.toString();
  }, [monthStart, monthEnd]);

  const { data: calendarAttendanceResponse, isLoading: loadingCalendarAttendance } = useQuery({
    queryKey: ['parent_student_attendance_calendar', studentId, format(calendarMonth, 'yyyy-MM')],
    queryFn: async () => {
      if (!studentId) return [];
      const response = await apiService.get(`/api/parent/students/${studentId}/attendance?${calendarParams}`);
      const payload = response.data || response;
      return payload?.records || [];
    },
    enabled: !!studentId && activeTab === 'attendance'
  });

  const { data: tableAttendanceResponse, isLoading: loadingTableAttendance } = useQuery({
    queryKey: ['parent_student_attendance_table', studentId, tableRange.start, tableRange.end],
    queryFn: async () => {
      if (!studentId) return [];
      const params = new URLSearchParams();
      if (tableRange.start) params.append('start_date', tableRange.start);
      if (tableRange.end) params.append('end_date', tableRange.end);
      const response = await apiService.get(`/api/parent/students/${studentId}/attendance?${params.toString()}`);
      const payload = response.data || response;
      return payload?.records || [];
    },
    enabled: !!studentId && activeTab === 'attendance'
  });

  const { data: attendanceSummaryResponse, isLoading: loadingAttendanceSummary } = useQuery({
    queryKey: ['parent_student_attendance_summary', studentId, tableRange.start, tableRange.end],
    queryFn: async () => {
      if (!studentId) return null;
      const params = new URLSearchParams();
      if (tableRange.start) params.append('start_date', tableRange.start);
      if (tableRange.end) params.append('end_date', tableRange.end);
      const response = await apiService.get(`/api/parent/students/${studentId}/attendance_summary?${params.toString()}`);
      return response.data || response;
    },
    enabled: !!studentId && activeTab === 'attendance'
  });

  const { data: paymentHistoryResponse, isLoading: loadingPayments } = useQuery({
    queryKey: ['parent_student_payments', studentId],
    queryFn: async () => {
      if (!studentId) return [];
      const response = await apiService.get(`/api/parent/fee_payments/${studentId}?limit=50&offset=0`);
      const payload = response.data || response;
      return payload?.data || [];
    },
    enabled: !!studentId && activeTab === 'payments'
  });

  const { data: syllabusResponse, isLoading: loadingSyllabus } = useQuery({
    queryKey: ['parent_student_syllabus', studentId, syllabusStart, syllabusEnd],
    queryFn: async () => {
      if (!studentId) return { data: [], total: 0 };
      const params = new URLSearchParams();
      if (syllabusStart) params.append('start_date', syllabusStart);
      if (syllabusEnd) params.append('end_date', syllabusEnd);
      params.append('limit', '100');
      const response = await apiService.get(`/api/parent/students/${studentId}/syllabus?${params.toString()}`);
      return response.data || { data: [], total: 0 };
    },
    enabled: !!studentId && activeTab === 'syllabus'
  });

  const { data: visionResponse, isLoading: loadingVision } = useQuery({
    queryKey: ['parent_student_vision_statements', studentId],
    queryFn: async () => {
      if (!studentId) return { data: [] };
      const response = await apiService.get(`/api/parent/students/${studentId}/vision_statements`);
      return response.data || { data: [] };
    },
    enabled: !!studentId && activeTab === 'vision'
  });

  const calendarAttendance = calendarAttendanceResponse || [];
  const tableAttendance = tableAttendanceResponse || [];
  const attendanceSummary = attendanceSummaryResponse || {
    total_days: 0,
    present_days: 0,
    absent_days: 0,
    late_days: 0,
    half_days: 0,
    attendance_percentage: 0
  };
  const paymentHistory = paymentHistoryResponse || [];
  const syllabusData = syllabusResponse?.data || [];
  const visionStatements = visionResponse?.data || [];

  const attendanceMap = useMemo(() => {
    const map = {};
    calendarAttendance.forEach((record) => {
      map[record.attendance_date] = record;
    });
    return map;
  }, [calendarAttendance]);

  const calendarWeeks = useMemo(() => {
    const start = startOfWeek(monthStart, { weekStartsOn: 1 });
    const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  }, [monthStart, monthEnd]);

  const handleApplyTableRange = () => {
    if (tableStartInput && tableEndInput && tableStartInput > tableEndInput) {
      toast.error('Start date cannot be after end date');
      return;
    }
    setTableRange({ start: tableStartInput, end: tableEndInput });
  };

  const formatCurrency = (amount) => {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Number.isNaN(numericAmount) || numericAmount === null || numericAmount === undefined) {
      return '₹0.00';
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(numericAmount);
  };

  const handleDownloadReceipt = (payment) => {
    if (!payment) return;

    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const margin = 15;
      const lineGap = 7;
      let cursorY = margin;

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(ENV_CONFIG.APP_NAME || 'School Name', 105, cursorY, { align: 'center' });

      cursorY += lineGap;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Fee Payment Receipt', 105, cursorY, { align: 'center' });

      cursorY += lineGap;
      doc.setLineWidth(0.5);
      doc.line(margin, cursorY, doc.internal.pageSize.getWidth() - margin, cursorY);
      cursorY += lineGap / 2;

      const infoRows = [
        ['Receipt Number', payment.receipt_number || 'N/A', 'Date', formatDisplayDate(payment.collection_date || payment.payment_date || payment.created_at, format(new Date(), 'dd MMMM yyyy'))],
        ['Student Name', payment.student_name || formValues?.student_name || '-', 'Roll Number', payment.roll_number || formValues?.roll_number || '-'],
        ['Grade / Division', `${payment.grade_name || formValues?.grade_name || '-'}${payment.division_name ? ` (${payment.division_name})` : formValues?.division_name ? ` (${formValues.division_name})` : ''}`, 'Collected By', payment.collected_by_name || 'N/A'],
        ['Payment Mode', (payment.payment_method || payment.payment_mode || '-').toString().toUpperCase(), 'Receipt Generated', formatDisplayDate(new Date())]
      ];

      autoTable(doc, {
        startY: cursorY,
        body: infoRows,
        theme: 'plain',
        styles: { fontSize: 11, cellPadding: 2 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 35 },
          1: { cellWidth: 60 },
          2: { fontStyle: 'bold', cellWidth: 35 },
          3: { cellWidth: 60 }
        }
      });

      let nextY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + lineGap : cursorY + lineGap * infoRows.length;

      autoTable(doc, {
        startY: nextY,
        head: [[ 'Sl. No', 'Description', 'Amount' ]],
        body: [[
          '1',
          payment.category_name || 'Fee Payment',
          formatCurrency(payment.amount)
        ]],
        styles: { fontSize: 11, halign: 'center' },
        headStyles: { fillColor: [46, 82, 163], halign: 'center' },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 110, halign: 'left' },
          2: { cellWidth: 35 }
        }
      });

      nextY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + lineGap : nextY + lineGap * 2;

      doc.setFont('helvetica', 'bold');
      doc.text(`Total Paid: ${formatCurrency(payment.amount)}`, margin, nextY);
      doc.setFont('helvetica', 'normal');
      nextY += lineGap;

      const remarks = payment.remarks ? payment.remarks.split('|').slice(-1)[0] : '';
      if (remarks) {
        doc.text(`Remarks: ${remarks}`, margin, nextY);
        nextY += lineGap;
      }

      doc.text('This is a computer generated receipt and does not require a signature.', margin, nextY + lineGap);

      const fileName = `${payment.receipt_number || 'fee_receipt'}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Receipt generation error:', error);
      toast.error('Failed to download receipt');
    }
  };

  if (!studentId) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div className="text-center py-5">
            <i className="bi bi-arrow-left-right display-4 text-muted mb-3"></i>
            <h5>Select a student to view details</h5>
            <p className="text-muted">Choose a child from the list to manage their information and records.</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (loadingStudentDetails || !formValues) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-white border-0">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1">{formValues.student_name}</h5>
            <div className="text-muted small">
              <span className="me-3">
                <i className="bi bi-hash me-1"></i>
                Roll: {formValues.roll_number || 'N/A'}
              </span>
              <span className="me-3">
                <i className="bi bi-mortarboard me-1"></i>
                {formValues.grade_name || 'Grade'} {formValues.division_name ? `- ${formValues.division_name}` : ''}
              </span>
              {formValues.admission_date && (
                <span>
                  <i className="bi bi-calendar-event me-1"></i>
                  Admitted: {formatDisplayDate(formValues.admission_date)}
                </span>
              )}
            </div>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'overview')} className="mb-4">
          <Tab eventKey="overview" title={<><i className="bi bi-person-lines-fill me-2"></i>Overview</>}>
            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Student Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formValues.student_name}
                      onChange={(e) => handleFormChange('student_name', e.target.value)}
                      maxLength={100}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Gender</Form.Label>
                    <Form.Select
                      value={formValues.gender}
                      onChange={(e) => handleFormChange('gender', e.target.value)}
                    >
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label className="text-muted">Residential Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formValues.residential_address}
                      onChange={(e) => handleFormChange('residential_address', e.target.value)}
                      maxLength={500}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="text-muted">Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      value={formValues.pincode}
                      onChange={(e) => handleFormChange('pincode', e.target.value)}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Samagra ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={formValues.sam_samagrah_id}
                      onChange={(e) => handleFormChange('sam_samagrah_id', e.target.value)}
                      maxLength={50}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Aapar ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={formValues.aapar_id}
                      onChange={(e) => handleFormChange('aapar_id', e.target.value)}
                      maxLength={50}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Emergency Contact Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={formValues.emergency_contact_number}
                      onChange={(e) => handleFormChange('emergency_contact_number', e.target.value)}
                      maxLength={15}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Emergency Contact Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formValues.emergency_contact_name}
                      onChange={(e) => handleFormChange('emergency_contact_name', e.target.value)}
                      maxLength={255}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Emergency Contact Relationship</Form.Label>
                    <Form.Control
                      type="text"
                      value={formValues.emergency_contact_relationship}
                      onChange={(e) => handleFormChange('emergency_contact_relationship', e.target.value)}
                      maxLength={255}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Travel Mode</Form.Label>
                    <Form.Select
                      value={formValues.travel_mode}
                      onChange={(e) => handleFormChange('travel_mode', e.target.value)}
                    >
                      <option value="">Select travel mode</option>
                      <option value="School Bus">School Bus</option>
                      <option value="Own">Own</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lg={4} md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Blood Group</Form.Label>
                    <Form.Select
                      value={formValues.blood_group}
                      onChange={(e) => handleFormChange('blood_group', e.target.value)}
                    >
                      <option value="">Select blood group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lg={8} md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Allergies</Form.Label>
                    <div className="form-control" style={{ minHeight: '48px', padding: '12px' }}>
                      <details>
                        <summary className="text-muted" style={{ cursor: 'pointer', listStyle: 'none' }}>
                          <i className="bi bi-chevron-down me-2"></i>
                          Select allergies...
                        </summary>
                        <div className="mt-3">
                          {ALLERGIES_OPTIONS.map((option) => {
                            const isChecked = Array.isArray(formValues.allergies) && formValues.allergies.includes(option);
                            return (
                              <div
                                key={option}
                                className="form-check"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleAllergyToggle(option)}
                              >
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {}}
                                />
                                <label className="form-check-label">
                                  {option}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    </div>
                    <div className="mt-2">
                      {Array.isArray(formValues.allergies) && formValues.allergies.length > 0 ? (
                        formValues.allergies.map((allergy) => (
                          <Badge key={allergy} bg="info" text="dark" className="me-2 mb-2">
                            {allergy}
                          </Badge>
                        ))
                      ) : (
                        <small className="text-muted">No allergies selected yet.</small>
                      )}
                    </div>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="text-muted">Medical Conditions</Form.Label>
                    <div className="d-flex flex-column gap-2">
                      <Form.Check
                        type="switch"
                        id="student-diabetic"
                        label="Diabetic"
                        checked={formValues.diabetic}
                        onChange={(e) => handleFormChange('diabetic', e.target.checked)}
                      />
                      <Form.Check
                        type="switch"
                        id="student-asthmatic"
                        label="Asthmatic"
                        checked={formValues.asthmatic}
                        onChange={(e) => handleFormChange('asthmatic', e.target.checked)}
                      />
                      <Form.Check
                        type="switch"
                        id="student-phobia"
                        label="Has Phobia"
                        checked={formValues.phobia}
                        onChange={(e) => handleFormChange('phobia', e.target.checked)}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label className="text-muted">Lifestyle Diseases / Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formValues.lifestyle_diseases}
                      onChange={(e) => handleFormChange('lifestyle_diseases', e.target.value)}
                      maxLength={500}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Family Doctor Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formValues.doctor_name}
                      onChange={(e) => handleFormChange('doctor_name', e.target.value)}
                      maxLength={100}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">Doctor Contact</Form.Label>
                    <Form.Control
                      type="text"
                      value={formValues.doctor_contact}
                      onChange={(e) => handleFormChange('doctor_contact', e.target.value)}
                      maxLength={15}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-muted">Clinic Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formValues.clinic_address}
                      onChange={(e) => handleFormChange('clinic_address', e.target.value)}
                      maxLength={500}
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-muted">Aspirations by Teacher</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formValues.student_aspirations}
                      disabled
                      maxLength={2000}
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="text-muted">Special Needs / Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formValues.special_need}
                      onChange={(e) => handleFormChange('special_need', e.target.value)}
                      maxLength={2000}
                    />
                  </Form.Group>
                </Col>

                <Col md={12} className="text-end">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={updateStudentMutation.isLoading}
                  >
                    {updateStudentMutation.isLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        Save Changes
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Tab>

          <Tab eventKey="attendance" title={<><i className="bi bi-calendar-check me-2"></i>Attendance Calendar</>}>
            <Row className="g-4">
              <Col>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </Button>
                      <h6 className="mb-0">{format(calendarMonth, 'MMMM yyyy')}</h6>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </Button>
                    </div>
                    {loadingCalendarAttendance ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : (
                      <div className="attendance-calendar">
                        <div className="table-responsive">
                          <Table bordered className="mb-0">
                            <thead>
                              <tr>
                                {weekDayLabels.map((day) => (
                                  <th key={day} className="text-center text-muted small">{day}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {calendarWeeks.map((week, index) => (
                                <tr key={index}>
                                  {week.map((day) => {
                                    const formattedDate = format(day, 'yyyy-MM-dd');
                                    const attendance = attendanceMap[formattedDate];
                                    const isCurrentMonth = isSameMonth(day, monthStart);
                                    const statusVariant = attendance ? ATTENDANCE_STATUS_VARIANTS[attendance.status] || 'secondary' : null;

                                    return (
                                      <td
                                        key={formattedDate}
                                        className={`align-top ${!isCurrentMonth ? 'bg-light text-muted' : ''}`}
                                        style={{ minWidth: '90px' }}
                                      >
                                        <div className={`d-flex justify-content-between small ${isSameDay(day, new Date()) ? 'fw-bold' : ''}`}>
                                          <span>{format(day, 'd')}</span>
                                        </div>
                                        {attendance ? (
                                          <Badge bg={statusVariant} className="mt-2 w-100 text-capitalize">
                                            {attendance.status.replace('_', ' ')}
                                          </Badge>
                                        ) : (
                                          <small className="text-muted d-block mt-2 text-center">-</small>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                        <div className="mt-3">
                          {Object.entries(ATTENDANCE_STATUS_VARIANTS).map(([status, variant]) => (
                            <Badge bg={variant} key={status} className="me-2 text-capitalize">
                              {status.replace('_', ' ')}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="attendanceReports" title={<><i className="bi bi-clipboard-data me-2"></i>Attendance Reports</>}>
            <Row className="g-4">
              <Col lg={12}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    {loadingAttendanceSummary ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : (
                      <Row className="g-3 mb-3">
                        <Col sm={6}>
                          <div className="p-3 bg-light rounded border h-100">
                            <div className="text-muted text-uppercase small">Total Days</div>
                            <div className="fs-5 fw-semibold">{attendanceSummary.total_days || 0}</div>
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="p-3 bg-light rounded border h-100">
                            <div className="text-muted text-uppercase small">Attendance Rate</div>
                            <div className="fs-5 fw-semibold">{attendanceSummary.attendance_percentage || 0}%</div>
                          </div>
                        </Col>
                        <Col sm={4}>
                          <div className="p-3 bg-light rounded border h-100 text-center">
                            <Badge bg="success" className="mb-2">Present</Badge>
                            <div className="fw-semibold">{attendanceSummary.present_days || 0}</div>
                          </div>
                        </Col>
                        <Col sm={4}>
                          <div className="p-3 bg-light rounded border h-100 text-center">
                            <Badge bg="danger" className="mb-2">Absent</Badge>
                            <div className="fw-semibold">{attendanceSummary.absent_days || 0}</div>
                          </div>
                        </Col>
                        <Col sm={4}>
                          <div className="p-3 bg-light rounded border h-100 text-center">
                            <Badge bg="warning" className="mb-2">Late</Badge>
                            <div className="fw-semibold">{attendanceSummary.late_days || 0}</div>
                          </div>
                        </Col>
                      </Row>
                    )}

                    <Form className="border rounded p-3 bg-light mb-3">
                      <Row className="g-3 align-items-end">
                        <Col md={5}>
                          <Form.Group>
                            <Form.Label className="text-muted small">Start Date</Form.Label>
                            <Form.Control
                              type="date"
                              value={tableStartInput}
                              onChange={(e) => setTableStartInput(e.target.value)}
                              max={tableEndInput || format(new Date(), 'yyyy-MM-dd')}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={5}>
                          <Form.Group>
                            <Form.Label className="text-muted small">End Date</Form.Label>
                            <Form.Control
                              type="date"
                              value={tableEndInput}
                              onChange={(e) => setTableEndInput(e.target.value)}
                              min={tableStartInput}
                              max={format(new Date(), 'yyyy-MM-dd')}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2} className="text-end">
                          <Button variant="primary" onClick={handleApplyTableRange}>
                            Apply
                          </Button>
                        </Col>
                      </Row>
                    </Form>

                    {loadingTableAttendance ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : tableAttendance.length === 0 ? (
                      <Alert variant="secondary" className="text-center">
                        <i className="bi bi-calendar-x display-4 text-muted d-block mb-3"></i>
                        No attendance records found for the selected range.
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table responsive hover size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Date</th>
                              <th>Status</th>
                              <th>Check-in</th>
                              <th>Check-out</th>
                              <th>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tableAttendance.map((record) => (
                              <tr key={`${record.attendance_date}-${record.id || record.status}`}>
                      <td>{formatDisplayDate(record.attendance_date)}</td>
                                <td>
                                  <Badge bg={ATTENDANCE_STATUS_VARIANTS[record.status] || 'secondary'} className="text-capitalize">
                                    {record.status.replace('_', ' ')}
                                  </Badge>
                                </td>
                                <td>{formatTime(record.check_in_time)}</td>
                                <td>{formatTime(record.check_out_time)}</td>
                                <td>{record.remarks || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="payments" title={<><i className="bi bi-receipt me-2"></i>Payments</>}>
            {loadingPayments ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : paymentHistory.length === 0 ? (
              <Alert variant="secondary" className="text-center">
                <i className="bi bi-receipt display-4 text-muted d-block mb-3"></i>
                No payment history found for this student.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table responsive hover>
                  <thead className="table-light">
                    <tr>
                      <th>Receipt #</th>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Amount</th>
                      <th>Payment Mode</th>
                      <th>Collected By</th>
                      <th>Remarks</th>
                      <th className="text-center">Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((payment) => (
                      <tr key={payment.id}>
                        <td><Badge bg="secondary">{payment.receipt_number}</Badge></td>
                        <td>{formatDisplayDate(payment.collection_date || payment.payment_date || payment.created_at)}</td>
                        <td>{payment.category_name || 'N/A'}</td>
                        <td>
                          <strong className="text-success">{formatCurrency(payment.amount)}</strong>
                        </td>
                        <td>
                          <Badge bg="info" className="text-uppercase">{payment.payment_method || payment.payment_mode || '-'}</Badge>
                        </td>
                        <td>{payment.collected_by_name || 'N/A'}</td>
                        <td>{payment.remarks ? payment.remarks.split('|').slice(-1)[0] : '-'}</td>
                        <td className="text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleDownloadReceipt(payment)}
                          >
                            <i className="bi bi-download me-1"></i>
                            PDF
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Tab>

          <Tab eventKey="syllabus" title={<><i className="bi bi-journal-text me-2"></i>Syllabus</>}>
            <Form className="border rounded bg-light p-3 mb-3">
              <Row className="g-3 align-items-end">
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className="text-muted small">Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={syllabusStart}
                      onChange={(e) => setSyllabusStart(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className="text-muted small">End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={syllabusEnd}
                      onChange={(e) => setSyllabusEnd(e.target.value)}
                      min={syllabusStart}
                    />
                  </Form.Group>
                </Col>
                <Col md={2} className="text-end">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setSyllabusStart('');
                      setSyllabusEnd('');
                    }}
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
            </Form>

            {loadingSyllabus ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : syllabusData.length === 0 ? (
              <Alert variant="secondary" className="text-center">
                <i className="bi bi-journal-x display-4 text-muted d-block mb-3"></i>
                No syllabus entries found for the selected filters.
              </Alert>
            ) : (
              <div className="d-flex flex-column gap-3">
                {syllabusData.map((item) => (
                  <Card key={item.id} className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{item.topic_title}</h6>
                          <div className="text-muted small mb-2">
                            <i className="bi bi-calendar-event me-1"></i>
                            {item.syllabus_date ? formatDisplayDate(item.syllabus_date) : 'N/A'}
                            <span className="ms-3"><i className="bi bi-hash me-1"></i>Day {item.day_number}</span>
                          </div>
                          {item.topic_description && (
                            <p className="text-muted mb-2">{item.topic_description}</p>
                          )}
                          <div className="d-flex gap-2 flex-wrap">
                            {item.subject_name && (
                              <Badge bg="primary" className="text-capitalize">{item.subject_name}</Badge>
                            )}
                            {item.video_link && (
                              <Badge as="a" href={item.video_link} target="_blank" rel="noopener noreferrer" bg="success">
                                <i className="bi bi-play-circle me-1"></i>Video
                              </Badge>
                            )}
                            {item.documents && (
                              <Badge as="a" href={item.documents.startsWith('http') ? item.documents : `/${item.documents}`} target="_blank" rel="noopener noreferrer" bg="info">
                                <i className="bi bi-file-earmark-text me-1"></i>Document
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Badge bg="secondary" className="text-capitalize">
                          {item.created_by_name ? `By ${item.created_by_name}` : 'Syllabus'}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </Tab>

          <Tab eventKey="vision" title={<><i className="bi bi-lightbulb me-2"></i>Vision</>}>
            {loadingVision ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : visionStatements.length === 0 ? (
              <Alert variant="secondary" className="text-center">
                <i className="bi bi-lightbulb-off display-4 text-muted d-block mb-3"></i>
                No vision statements available for this grade yet.
              </Alert>
            ) : (
              <div className="d-flex flex-column gap-3">
                {visionStatements.map((statement) => (
                  <Card key={statement.id} className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{statement.grade_id === 0 || !statement.grade_name || statement.grade_name === 'N/A' ? 'Common Vision Statement' : `Grade: ${statement.grade_name}`}</h6>
                          <p className="mb-2 text-muted">{statement.vision}</p>
                          <small className="text-muted">
                            <i className="bi bi-person-badge me-1"></i>
                            {statement.staff_name || 'Staff'}
                            {statement.created_date && ` • ${formatDisplayDate(statement.created_date)}`}
                          </small>
                        </div>
                        <i className="bi bi-quote display-4 text-primary opacity-25"></i>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </Tab>
        </Tabs>
      </Card.Body>
    </Card>
  );
};

export default ParentStudentDetails;

